import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Barra de rolagem horizontal fixa na borda inferior da janela, espelhando o
 * scroll de um container interno. Serve para qualquer área do CRM cuja
 * largura ultrapassa a viewport (Kanban do Funil, tabelas largas etc.).
 *
 * Uso:
 *   const ref = useRef();
 *   <div ref={ref} className="overflow-x-auto">...</div>
 *   <StickyHScrollbar targetRef={ref} />
 *
 * A barra aparece apenas quando: o container está na tela + tem overflow
 * horizontal + a barra de rolagem nativa está fora da viewport (abaixo do fim).
 * Sincronização bidirecional (arrastar a barra move o container e vice-versa).
 */
export default function StickyHScrollbar({ targetRef }) {
  const barRef = useRef(null);
  const suppress = useRef(false);
  const [state, setState] = useState({ visible: false, left: 0, width: 0, contentW: 0 });

  useLayoutEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const measure = () => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const hasOverflow = el.scrollWidth > el.clientWidth + 2;
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      const nativeBarOffscreen = rect.bottom > window.innerHeight; // fim do container fora da viewport
      const visible = hasOverflow && inView && nativeBarOffscreen;
      setState({
        visible,
        left: rect.left,
        width: rect.width,
        contentW: el.scrollWidth,
      });
    };

    const onElScroll = () => {
      if (!barRef.current) return;
      if (suppress.current) { suppress.current = false; return; }
      suppress.current = true;
      barRef.current.scrollLeft = el.scrollLeft;
    };

    measure();
    // Remedeia após um tick porque quando o container ficava `display:none`
    // e passa a `block` (ex.: troca de visão), a primeira leitura do
    // getBoundingClientRect() pode devolver 0.
    const retryT = setTimeout(measure, 200);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Observa mudanças de layout do próprio DOM (colunas adicionadas etc.).
    const mo = new MutationObserver(measure);
    mo.observe(el, { subtree: true, childList: true, attributes: true });

    el.addEventListener("scroll", onElScroll, { passive: true });
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    return () => {
      clearTimeout(retryT);
      ro.disconnect();
      mo.disconnect();
      el.removeEventListener("scroll", onElScroll);
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [targetRef]);

  // Ao montar/desmontar a barra visível, sincroniza a posição inicial com o alvo.
  useEffect(() => {
    if (state.visible && barRef.current && targetRef.current) {
      barRef.current.scrollLeft = targetRef.current.scrollLeft;
    }
  }, [state.visible, targetRef]);

  const onBarScroll = () => {
    if (!targetRef.current || !barRef.current) return;
    if (suppress.current) { suppress.current = false; return; }
    suppress.current = true;
    targetRef.current.scrollLeft = barRef.current.scrollLeft;
  };

  if (!state.visible) return null;
  return (
    <div
      ref={barRef}
      onScroll={onBarScroll}
      data-testid="sticky-hscroll"
      className="fixed bottom-0 z-40 overflow-x-scroll overflow-y-hidden bg-white/95 backdrop-blur border-t border-black/10 shadow-[0_-2px_10px_rgb(0,0,0,0.06)]"
      style={{ left: state.left, width: state.width, height: 16 }}
    >
      <div style={{ width: state.contentW, height: 1 }} />
    </div>
  );
}
